import { RedirectToSignIn, SignedIn, SignedOut } from "@clerk/clerk-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import type {
  AccountDeleteOutput,
  AccountExportOutput,
} from "@panelgenie/contracts";
import {
  AccountDeletePanel,
  type AccountDeleteState,
} from "../../components/account/AccountDeletePanel.js";
import {
  AccountExportPanel,
  type AccountExportState,
} from "../../components/account/AccountExportPanel.js";
import {
  FeedbackPanel,
  type FeedbackPanelState,
} from "../../components/account/FeedbackPanel.js";
import { ThemeSwitcher } from "../../components/settings/ThemeSwitcher.js";
import { Heading } from "@panelgenie/shared-ui";
import { generateId } from "../../domain/id.js";
import {
  hardNavigate,
  readCurrentRoute,
} from "../../seams/crashReporter/index.js";
import { buildPlatformGlobals } from "../../seams/platform-globals/index.js";

/**
 * Phase 14a — Settings → Account index route (the live, authenticated
 * surface at `/settings/account/`).
 *
 * The route renders the three affordances (export + delete + feedback)
 * behind their confirmation modals per the slice spec. The export and
 * delete flows route through the new `account.*` tRPC procedures
 * (Phase 14a server slice); feedback rides Phase 14d. On a successful
 * delete, the route hard-navigates to `/settings/account/deleted` for
 * the anonymous confirmation surface.
 *
 * Lives at `/settings/account/` (index) rather than `/settings/account`
 * (parent) because the parent is now layout-only — see the
 * `settings.account.tsx` header for the routing rationale.
 *
 * The download seam (`platform-globals.pdfDownload`) handles both
 * blob downloads — the `.json` dump (utf-8 payload) and the `.zip`
 * photo archive (base64 payload).
 *
 * Auth-gated through the `<SignedIn>` / `<SignedOut>` pattern the
 * other authenticated routes use.
 */
export const Route = createFileRoute("/settings/account/")({
  component: SettingsAccountIndexPage,
});

function SettingsAccountIndexPage() {
  return (
    <>
      <SignedIn>
        <SettingsAccountContent />
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

function SettingsAccountContent() {
  const { trpc, queryClient, crashReporter } = Route.useRouteContext();
  const _queryClientHandle = useQueryClient();
  const [services] = useState(() => buildPlatformGlobals({ trpc }));

  const [exportState, setExportState] = useState<AccountExportState>({
    kind: "idle",
  });
  const [deleteState, setDeleteState] = useState<AccountDeleteState>({
    kind: "idle",
  });
  const [feedbackState, setFeedbackState] = useState<FeedbackPanelState>({
    kind: "idle",
  });

  const exportMutation = useMutation({
    mutationFn: async (): Promise<AccountExportOutput> => {
      // The tRPC client erases the `dump: unknown` to `dump?:
      // unknown` because `unknown` is structurally optional on the
      // wire. The server's procedure return type guarantees the
      // field is always present; cast through the contract type so
      // downstream code sees the non-optional shape.
      const r = await trpc.account.export.query();
      return r as AccountExportOutput;
    },
    onMutate: () => {
      setExportState({ kind: "exporting" });
    },
    onSuccess: async (result) => {
      // Two artifacts: the `.json` dump (utf-8) and the `.zip` photo
      // archive (base64). Both ride the inline-blob download seam.
      // The JSON's content type is `application/json`; the ZIP is
      // `application/zip`. Filenames carry the export instant so
      // repeated exports do not overwrite each other on disk.
      const stamp = result.exportedAt.replace(/[:.]/g, "-");
      const jsonText = JSON.stringify(result.dump, null, 2);
      const jsonOutcome = await services.pdfDownload.downloadInlineBlob({
        filename: `panelgenie-account-${stamp}.json`,
        contentType: "application/json",
        payload: { kind: "utf8", text: jsonText },
      });
      if (jsonOutcome.kind === "failure") {
        setExportState({ kind: "error", message: jsonOutcome.message });
        return;
      }
      if (result.bundleByteLength > 0) {
        const zipOutcome = await services.pdfDownload.downloadInlineBlob({
          filename: `panelgenie-photos-${stamp}.zip`,
          contentType: "application/zip",
          payload: {
            kind: "base64",
            base64: result.photosBundleBase64,
          },
        });
        if (zipOutcome.kind === "failure") {
          setExportState({ kind: "error", message: zipOutcome.message });
          return;
        }
      }
      setExportState({ kind: "ready", result });
    },
    onError: (err) => {
      setExportState({
        kind: "error",
        message:
          err instanceof Error
            ? err.message
            : "Couldn't prepare your export. Try again in a moment.",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (
      typedPhrase: string,
    ): Promise<AccountDeleteOutput> => {
      return trpc.account.delete.mutate({
        idempotencyKey: generateId("idem"),
        confirmationPhrase: typedPhrase,
      });
    },
    onMutate: () => {
      setDeleteState({ kind: "deleting" });
    },
    onSuccess: async () => {
      setDeleteState({ kind: "deleted" });
      // Clear every tRPC cache — the post-delete session must not
      // surface any of the deleted principal's data on the brief
      // window before the hard-navigate below tears down the JS
      // context.
      await queryClient.clear();
      // Hard-navigate to the anonymous closeout surface. A
      // router-level `navigate` here was previously preempted by the
      // parent route's `<SignedOut><RedirectToSignIn />` arm during
      // the React re-render that flipped when `clerk.session`
      // became null, AND by Clerk's own `signOut()` redirect to the
      // configured `signInUrl`. A hard browser load to
      // `/settings/account/deleted` (which has no Clerk gate)
      // commits the URL change atomically before either teardown
      // path can fire — the parent `settings.account` layout is
      // now `<Outlet />`-only and auth-anonymous, so the deleted
      // child mounts cleanly.
      //
      // We deliberately do NOT call `clerk.signOut()` here: the
      // server-side cascade already deleted the principal, and the
      // hard-navigate destroys the JS context (and the in-memory
      // Clerk session-state along with it). A returning user who
      // signs in again will hit the post-delete 404 from
      // `account.delete`'s existence guard; their authenticated
      // tRPC reads will return empty payloads against an emptied
      // owner-scoped repo set. The signOut was previously paired
      // with a router-level navigate that was racing the redirect;
      // dropping it removes the race entirely.
      hardNavigate("/settings/account/deleted");
    },
    onError: (err) => {
      setDeleteState({
        kind: "error",
        message:
          err instanceof Error
            ? err.message
            : "Couldn't delete your account. Try again in a moment.",
      });
    },
  });

  /**
   * Phase 14d — `account.feedback` mutation. Threads the Phase 14c
   * crash-reporter session id verbatim through the wire so the
   * dashboard can join feedback rows against the crash-event stream
   * (`MVP.md` §6 DoD item 8). The route is captured at submission time
   * from `window.location.pathname` *through the route's
   * `Route.useMatch` self-reference*, so the affordance always reports
   * the actual path the user is on (`/settings/account` at the MVP,
   * but a future floating feedback button could surface the affordance
   * anywhere).
   */
  const feedbackMutation = useMutation({
    mutationFn: async (message: string) => {
      const sessionId = crashReporter.currentSessionId();
      // The current route resolves via the `readCurrentRoute` seam
      // helper (`window.location.*` reaches are confined to `seams/`).
      const route = readCurrentRoute();
      return trpc.account.feedback.mutate({
        idempotencyKey: generateId("idem"),
        sessionId,
        message,
        route,
      });
    },
    onMutate: () => {
      setFeedbackState({ kind: "submitting" });
    },
    onSuccess: () => {
      setFeedbackState({ kind: "submitted" });
    },
    onError: (err) => {
      setFeedbackState({
        kind: "error",
        message:
          err instanceof Error
            ? err.message
            : "Couldn't send your feedback. Try again in a moment.",
      });
    },
  });

  return (
    <main
      aria-label="settings-account"
      className="mx-auto max-w-3xl px-4 py-6 flex flex-col gap-6"
    >
      <Heading level="h1">Account</Heading>
      <p className="text-base text-fg">
        Manage your data. Both actions are available on every tier — see{" "}
        <a href="/policy/data" className="text-accent hover:underline">
          our data policy
        </a>{" "}
        for the full SLA.
      </p>
      <AccountExportPanel
        state={exportState}
        slaCopy={
          exportState.kind === "ready"
            ? exportState.result.slaCopy
            : "We do not retain a copy of the export on our servers."
        }
        onExport={() => exportMutation.mutate()}
      />
      <AccountDeletePanel
        state={deleteState}
        slaCopy="Deletion completes within 30 days; you'll receive a confirmation email."
        onDelete={(typedPhrase) => deleteMutation.mutate(typedPhrase)}
      />
      <FeedbackPanel
        state={feedbackState}
        onSubmit={(message) => feedbackMutation.mutate(message)}
      />
      <ThemeSwitcher />
    </main>
  );
}
